import React, { useEffect, useState } from "react";
import { Radio, Newspaper, Info, MessageSquare, ArrowUpRight } from "lucide-react";
import { api } from "../../lib/api";
import { D, JAKARTA } from "./ui";
import { RadioPanel } from "./RadioPanel";
import { KoranPanel } from "./KoranPanel";
import { InfoPanel } from "./InfoPanel";
import { PesanPanel } from "./PesanPanel";

// Pusat Konten di Dashboard: 4 kartu ringkasan (Radio, Koran, Info, Pesan Warga) yang membuka panel penuh di tempat
const ITEMS = [
  { key: "radio", label: "Radio", icon: Radio, Panel: RadioPanel },
  { key: "koran", label: "Koran", icon: Newspaper, Panel: KoranPanel },
  { key: "info", label: "Info", icon: Info, Panel: InfoPanel },
  { key: "pesan", label: "Pesan Warga", icon: MessageSquare, Panel: PesanPanel },
];
export const CONTENT_PANELS = Object.fromEntries(ITEMS.map(i => [i.key, i.Panel]));

// Ringkasan angka tiap konten — dimuat ulang setiap kembali dari panel (refreshKey)
function useContentSummary(token, refreshKey) {
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    Promise.all([api.adminRadio(token), api.adminKoran(token), api.adminInfo(token), api.adminPesan(token)])
      .then(([r, k, i, p]) => {
        if (!alive) return;
        const tracks = r.tracks || [], ann = r.announcements || [], arts = k.articles || [], pages = i.pages || [], feed = p.pesan || [];
        setS({
          radio: { main: `${tracks.filter(t => t.active).length} lagu aktif`, sub: `${ann.filter(a => a.active).length} pengumuman`, latest: tracks[0]?.title, empty: "Playlist masih kosong" },
          koran: { main: `${arts.filter(a => a.status === "published").length} tayang`, sub: `${arts.filter(a => a.status === "draft").length} draf`, latest: arts[0]?.title, empty: "Belum ada artikel" },
          info: { main: `${pages.filter(x => x.published).length} tayang`, sub: `${pages.length} halaman`, latest: pages[0]?.title, empty: "Belum ada halaman" },
          pesan: { main: `${p.unread || 0} belum dibaca`, sub: `${feed.length} pesan`, latest: feed[0]?.text, empty: "Belum ada pesan", alert: (p.unread || 0) > 0 },
        });
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [token, refreshKey]);
  return s;
}

export function ContentCards({ token, open, onOpen, compact, refreshKey }) {
  const sum = useContentSummary(token, refreshKey);
  return (
    <div data-testid="content-cards" style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: compact ? 10 : 14, fontFamily: JAKARTA }}>
      {ITEMS.map(({ key, label, icon: Icon }) => {
        const on = open === key, d = sum?.[key];
        return (
          <button key={key} data-testid={`content-card-${key}`} onClick={() => onOpen(on ? null : key)} aria-pressed={on}
            style={{
              position: "relative", textAlign: "left", border: 0, cursor: "pointer", fontFamily: JAKARTA,
              borderRadius: compact ? 20 : 26, padding: compact ? "12px 14px" : "18px 20px 16px",
              background: on ? D.dark : "#fff", color: on ? "#fff" : D.ink,
              boxShadow: on ? "0 12px 26px rgba(20,20,20,0.18)" : "0 8px 22px rgba(20,20,20,0.05)",
              transition: "transform .15s, box-shadow .15s, background-color .15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 14px 30px rgba(20,20,20,0.14)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = on ? "0 12px 26px rgba(20,20,20,0.18)" : "0 8px 22px rgba(20,20,20,0.05)"; }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: compact ? 30 : 36, height: compact ? 30 : 36, borderRadius: 11, background: on ? "rgba(255,255,255,0.12)" : D.blueSoft, display: "grid", placeItems: "center", flex: "none", position: "relative" }}>
                <Icon size={compact ? 14 : 16} color={on ? "#9f9fff" : D.blue} />
                {d?.alert && <span data-testid={`content-alert-${key}`} style={{ position: "absolute", top: -3, right: -3, width: 10, height: 10, borderRadius: 99, background: D.danger, border: "2px solid #fff" }} />}
              </span>
              <b style={{ fontSize: compact ? 13 : 14.5, flex: 1 }}>{label}</b>
              {compact
                ? <span style={{ fontSize: 11, fontWeight: 700, opacity: on ? 0.75 : 1, color: on ? "#fff" : D.sub, whiteSpace: "nowrap" }}>{d ? d.main : "…"}</span>
                : <span style={{ width: 30, height: 30, borderRadius: "50%", border: `1.5px solid ${on ? "rgba(255,255,255,0.5)" : "#cfcaba"}`, display: "grid", placeItems: "center" }}><ArrowUpRight size={14} /></span>}
            </div>
            {!compact && (
              <>
                <div style={{ marginTop: 14, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                  <span data-testid={`content-main-${key}`} style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: on ? "#fff" : (d?.alert ? D.danger : D.ink) }}>{d ? d.main : "Memuat…"}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: on ? "rgba(255,255,255,0.7)" : D.sub }}>{d?.sub}</span>
                </div>
                <p style={{ margin: "8px 0 0", fontSize: 11.5, color: on ? "rgba(255,255,255,0.7)" : D.sub, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontStyle: d?.latest ? "normal" : "italic" }}>
                  {d ? (d.latest || d.empty) : "\u00a0"}
                </p>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
